import classNames from 'classnames';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PriceVO } from '@ctct/domain-layer';
import { Skeleton } from '@ctct/payflow-ui';
import { Alert, Col, Row } from '@ctct/presentation-layer';

import applepayIcon from './assets/apple_logo.svg';
import type { PaymentMethodsLoadedProps } from '../widgets/PaymentMethodsWidget/types';

interface ApplePayProps {
  data: Pick<
    PaymentMethodsLoadedProps['data'],
    | 'getIsApplePaySupportedInBrowser'
    | 'getApplePayCreditCardName'
    | 'getApplePayCreditCardNumberFormatted'
    | 'getApplePayCreditCardExpirationDate'
    | 'getApplePayURL'
    | 'getApplePaySecurePayIframeId'
  >;
  handlers: Pick<
    PaymentMethodsLoadedProps['handlers'],
    | 'onStartListeningApplePaySecurePay'
    | 'onStopListeningApplePaySecurePay'
    | 'onApplePaySecurePayIframeLoad'
    | 'setApplePayIsLoaded'
    | 'onApplePaySecurePayIframeError'
  >;
  hooks: Pick<PaymentMethodsLoadedProps['hooks'], 'useApplePaySecurePayIframeState'>;
  shouldGetAddress?: boolean;
  shouldGetTaxUpdate?: boolean;
}

export const ApplePay = ({ data, handlers, hooks, shouldGetAddress, shouldGetTaxUpdate }: ApplePayProps) => {
  const { t } = useTranslation();

  const {
    applePayAddedCssToIframe,
    applePaySecurePayIframeBlockedMessage,
    applePayTokenExpired,
    applePayTotalChanged,
    applePayConnected,
    applePayZeroValuePayment,
  } = hooks.useApplePaySecurePayIframeState();

  const isReady = applePayAddedCssToIframe;

  const shouldShowIframe = isReady && !applePayZeroValuePayment;

  const isApplePaySupportedInBrowser = data.getIsApplePaySupportedInBrowser();

  const getErrors = () => {
    if (applePayZeroValuePayment) {
      const zeroPrice = new PriceVO(0);
      const formattedZeroPrice = zeroPrice.format(applePayZeroValuePayment.currency);

      return (
        <Alert variant="warning" message={t('payment-methods.apple_pay_zero_value_payment', { formattedZeroPrice })} />
      );
    }

    if (applePayTotalChanged) {
      return <Alert variant="warning" message={t('payment-methods.apple_pay_checkout_total_changed')} />;
    }

    if (applePayTokenExpired) {
      return <Alert variant="warning" message={t('payment-methods.apple_pay_token_timeout')} />;
    }

    return null;
  };

  useEffect(() => {
    handlers.onStartListeningApplePaySecurePay();

    return () => {
      handlers.onStopListeningApplePaySecurePay();
    };
  }, [handlers]);

  useEffect(() => {
    return () => {
      handlers.setApplePayIsLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // in this state token for the given transaction has been generated,
  // but apple pay has not been saved as a payment method
  if (applePayConnected) {
    return (
      <div className="apple-pay-credit-card-info">
        <div className="card-info-line">
          <span>{`${data.getApplePayCreditCardName()} ${data.getApplePayCreditCardNumberFormatted()}`}</span>
          <div className="apple-pay-logo-wrapper">
            <img
              src={applepayIcon}
              alt={t('payment-methods.apple_pay')}
              data-qe-id="applepay-icon"
              className="payment-method-img"
            />
          </div>
        </div>
        <p>
          {t('payment-methods.apple_pay_credit_card_expiration_date', {
            expirationDate: data.getApplePayCreditCardExpirationDate(),
          })}
        </p>
        <div className="apple-pay-agreement-info">{t('payment-methods.apple_pay_agreement')}</div>
      </div>
    );
  }

  if (!isApplePaySupportedInBrowser) {
    return <Alert message={t('payment-methods.apple_pay_not_supported_error')} />;
  }

  if (applePaySecurePayIframeBlockedMessage) {
    return <Alert message={t('payment-methods.credit_card_loading_error')} />;
  }

  return (
    <div>
      <Row className={classNames(applePayZeroValuePayment && 'apple-pay-zero-value-payment')}>
        <Col>
          {isReady ? null : <Skeleton height={50} />}
          <div className="applepay-iframe-wrapper">
            <iframe
              allow="payment"
              scrolling="no"
              src={data.getApplePayURL(shouldGetAddress, shouldGetTaxUpdate)}
              onLoad={handlers.onApplePaySecurePayIframeLoad}
              onError={handlers.onApplePaySecurePayIframeError}
              data-qe-id={data.getApplePaySecurePayIframeId()}
              id={data.getApplePaySecurePayIframeId()}
              className="applepay-iframe"
              title="applepay"
              loading="eager"
              style={
                // previously we used display: none; to hide iframe but it could cause issues with iframe loading, so now we use opacity and position to achieve the same effect
                shouldShowIframe
                  ? {
                      opacity: 1,
                      position: 'static',
                      top: 'auto',
                      left: 'auto',
                    }
                  : {
                      opacity: 0,
                      position: 'absolute',
                      top: '0',
                      left: '0',
                    }
              }
            />
          </div>
        </Col>
      </Row>
      {getErrors()}
    </div>
  );
};
